import { ArrowRight, CheckCircle } from "lucide-react";
import HeroBreadcrumb from "@/components/layout/HeroBreadcrumb";

const VANTAGGI = [
  { titolo: "Percorsi Su Misura", desc: "Formazione progettata su misura per rispondere alle reali esigenze della tua Azienda." },
  { titolo: "Zero Costi Diretti", desc: "Grazie al finanziamento del Fondo For.Te e FONDIR, la tua Azienda forma i lavoratori senza sostenere costi diretti." },
  { titolo: "Sviluppo Mirato delle Competenze", desc: "Ogni percorso è pensato per aumentare la produttività e l'efficienza del personale, in linea con gli obiettivi strategici." },
  { titolo: "Flessibilità e Adattabilità", desc: "La formazione è flessibile e può essere adattata in qualsiasi momento ai cambiamenti delle esigenze aziendali." },
];

const STEPS = [
  { n: "1", titolo: "Adesione al Fondo", desc: "Aderisci gratuitamente al Fondo For.Te e a FONDIR. Ti guidiamo in ogni fase del processo." },
  { n: "2", titolo: "Identificazione delle Esigenze Formative", desc: "Analizziamo insieme le necessità formative della tua Azienda costruendo un piano coerente con le tue priorità." },
  { n: "3", titolo: "Progettazione della Formazione Personalizzata", desc: "Definiamo contenuti, durata e modalità di erogazione (in aula, online, blended) del piano formativo." },
  { n: "4", titolo: "Richiesta di Finanziamento", desc: "Gestiamo per te la richiesta di finanziamento tramite il Fondo For.Te e FONDIR, semplificando l'iter burocratico." },
  { n: "5", titolo: "Erogazione della Formazione", desc: "Approvato il piano, il corso viene erogato da enti formativi e docenti accreditati. La formazione può essere in aula o a distanza." },
  { n: "6", titolo: "Monitoraggio e Rendicontazione", desc: "Gestiamo tutto il processo di monitoraggio e rendicontazione, garantendo la conformità alle normative." },
  { n: "7", titolo: "Certificazione delle Competenze", desc: "Al termine della formazione, i partecipanti ricevono una certificazione delle competenze acquisite." },
];

const BENEFICI_DIRETTI = [
  "Supporto completo in ogni fase",
  "Aggiornamento e innovazione continua del personale",
  "Master e percorsi di alta formazione per dirigenti (tramite FONDIR)",
  "Costi azzerati grazie ai fondi interprofessionali",
];

export default function FormazionePersonalizzataPage() {
  return (
    <main id="main-content" data-testid="formazione-personalizzata-page">
      {/* ── HERO ── */}
      <section className="relative h-[300px] md:h-[360px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=1920&q=60&auto=format"
          alt="Formazione personalizzata For.Te FONDIR"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.85)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <HeroBreadcrumb />
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed'] mb-2">Formazione</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white font-['Encode_Sans_Semi_Condensed'] tracking-tight">
            Formazione Personalizzata con il Fondo For.Te e FONDIR
          </h1>
        </div>
      </section>

      {/* ── INTRO ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-lg text-[var(--confcom-text-primary)] font-semibold font-['Encode_Sans_Semi_Condensed'] leading-relaxed mb-5">
            Formazione su misura per la tua Azienda, finanziata dai Fondi Paritetici Interprofessionali.
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed mb-4">
            La <strong className="text-[var(--confcom-text-primary)]">formazione personalizzata</strong> è un'opportunità fondamentale per le aziende che vogliono sviluppare competenze specifiche del proprio personale. Con il supporto di Confcommercio Roma e Promo.Ter puoi accedere a percorsi formativi progettati su misura <strong className="text-[var(--confcom-text-primary)]">senza costi diretti</strong>, grazie al <strong className="text-[var(--confcom-text-primary)]">Fondo For.Te</strong> (per dipendenti) e a <strong className="text-[var(--confcom-text-primary)]">FONDIR</strong> (per dirigenti).
          </p>
          <p className="text-[var(--confcom-text-secondary)] leading-relaxed">
            Che si tratti di potenziare competenze tecniche, aggiornare il personale su nuove normative o rafforzare le soft skills, i corsi vengono progettati esclusivamente per la tua realtà. I dirigenti possono inoltre frequentare <strong className="text-[var(--confcom-text-primary)]">master e percorsi di alta formazione</strong> tramite il Fondir.
          </p>
        </div>
      </section>

      {/* ── VANTAGGI ── */}
      <section className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">I Vantaggi della Formazione Personalizzata</h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {VANTAGGI.map((v) => (
              <div key={v.titolo} className="bg-white rounded-xl p-6 border border-[var(--confcom-border)]">
                <h3 className="text-base font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-2">{v.titolo}</h3>
                <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COME FUNZIONA + BENEFICI DIRETTI ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-8">Come Funziona con Confcommercio Roma</h2>
              <div className="space-y-10">
                {STEPS.map((s) => (
                  <div key={s.n} className="flex gap-4">
                    <div className="w-8 h-8 bg-[var(--confcom-blue)] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm font-['Encode_Sans_Semi_Condensed']">{s.n}</div>
                    <div>
                      <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-1">{s.titolo}</h3>
                      <p className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed mb-4">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[var(--confcom-bg-subtle)] rounded-2xl p-8 border border-[var(--confcom-border)] h-fit">
              <h3 className="text-lg font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] mb-5">Benefici Diretti per la Tua Azienda</h3>
              <div className="space-y-3">
                {BENEFICI_DIRETTI.map((b) => (
                  <div key={b} className="flex items-start gap-3">
                    <CheckCircle size={18} className="text-[var(--confcom-blue)] shrink-0 mt-0.5" />
                    <span className="text-sm text-[var(--confcom-text-secondary)] leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA standard ── */}
      <section className="py-12 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[var(--confcom-blue)] rounded-2xl px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-5">
            <div>
              <p className="text-white font-bold text-xl font-['Encode_Sans_Semi_Condensed'] mb-2">Vuoi attivare subito il tuo percorso formativo e accedere a nuove opportunità? Contattaci!</p>
              <div className="text-white/75 text-sm space-y-1">
                <p>
                  <a href="mailto:commerciale.promoter@confcommercioroma.it" className="hover:text-white transition-colors">commerciale.promoter@confcommercioroma.it</a>
                  {" · "}
                  <a href="mailto:areagestione@confcommercioroma.it" className="hover:text-white transition-colors">areagestione@confcommercioroma.it</a>
                </p>
                <p>
                  <a href="tel:+390668437606" className="hover:text-white transition-colors">0668437 606</a>
                  {" · "}
                  <a href="tel:+390668437530" className="hover:text-white transition-colors">0668437 530</a>
                  {" · "}
                  <a href="tel:+390668437457" className="hover:text-white transition-colors">0668437 457</a>
                </p>
              </div>
            </div>
            <a
              href="https://forms.gle/98CnoTiUAdGHJ9e88"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-[var(--confcom-blue)] px-7 py-3 rounded-md font-bold text-sm hover:opacity-90 transition-opacity whitespace-nowrap"
              data-testid="cta-formazione-personalizzata"
            >
              Richiedi maggiori informazioni <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
